import { object, ObjectSchema, string } from 'yup';
import * as yup from 'yup';

export const projectNameError =
  'Must start with a letter and end with a letter or number. Can contain letters, numbers, spaces, hyphens, and underscores.';

export const projectNameSchema = string()
  .required('This field is required')
  .max(40, 'Must be less than 40 characters')
  .matches(/^[a-zA-Z][ a-zA-Z0-9_-]*[a-zA-Z0-9]$/, {
    excludeEmptyString: true,
    message: projectNameError,
  });

export const apiKeyNicknameSchema = string()
  .required('This field is required')
  .min(2, 'Must be a minimum of 2 characters')
  .max(50, 'Must be less than 50 characters')
  .matches(/^[A-Za-z][A-Za-z0-9\-_]*$/, {
    excludeEmptyString: true,
    message:
      'Must begin with a letter and can only contain letters, numbers, hyphens, and underscores',
  });

// matches notification service email validation
export const validEmailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/i;

export function generateProjectNameSchema(
  fields: Record<string, any>,
  existingNames?: string[],
): ObjectSchema<any> {
  const objShape = Object.keys(fields).reduce((acc, field) => {
    acc[field] = projectNameSchema.test(
      'is-unique',
      'Project name already exists',
      (value) => !existingNames?.includes(value),
    );
    return acc;
  }, {});

  return object().shape(objShape);
}

export function isHttpsUrl(urlStr: string) {
  let url: URL;

  try {
    url = new URL(urlStr);
  } catch (_) {
    return false;
  }
  return url.protocol === 'https:';
}

// https://stackoverflow.com/a/5717133
const urlPattern = new RegExp(
  '^(https?:\\/\\/)?' + // protocol
    '(([a-z\\d]+(-[a-z\\d]+)*\\.)+[a-z]{2,}|' + // domain name with improved handling
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?' + // port
    '(\\/[\\w%_.~-]*)*' + // path
    '(\\?[;&\\w%_.~=+-]*)?' + // query string
    '(\\#[\\w_]*)?$', // fragment locator
  'i',
);

const localhostPattern = /^(http:\/\/)?localhost:\d+(\/.*)?(\/?\?[^\s]*)?$/;
const appSchemePattern = /^[a-zA-Z0-9]+:\/\/[\w/%.-]+(\/?\?[^\s]*)?$/;

export function isValidURI(uri: string): boolean {
  const urlRegex = urlPattern;
  return uri.trim() === '' || urlRegex.test(uri.trim());
}

export function validateRedirectURIs(uris: string): boolean {
  const uriArray = uris.split(/\r?\n/);

  return uriArray.every((uri) => {
    const trimmedUri = uri.trim();

    const isStandardUrl = urlPattern.test(trimmedUri);
    const isLocalhost = localhostPattern.test(trimmedUri);
    const isAppScheme = appSchemePattern.test(trimmedUri);

    return isStandardUrl || isLocalhost || isAppScheme;
  });
}

export function getRedirectUrisError(value: string) {
  if (/coinbase/i.test(value)) {
    return 'Redirect URIs cannot contain "Coinbase"';
  }
  if (!validateRedirectURIs(value)) {
    return 'Redirect URI is not valid';
  }
  return '';
}

export function validateName(input: string): boolean {
  if (!/^[a-zA-Z0-9].*[a-zA-Z0-9]$/.test(input)) {
    return false;
  }

  const hasSpecialCharacters = /[^a-zA-Z0-9 ]/.test(input);
  if (hasSpecialCharacters) {
    return false;
  }

  const spaceCount = (input.match(/ /g) || []).length;
  if (spaceCount > 5) {
    return false;
  }

  const containsCoinbase = /coinbase/i.test(input);
  if (containsCoinbase) {
    return false;
  }

  return true;
}

const onrampDomainSchema = yup
  .string()
  .required()
  .test('is-valid-url', 'Must be a valid URL with a schema and domain', (value) => {
    if (!value) return false; // Handle undefined or null values

    try {
      const url = new URL(value);

      // Ensure there's a protocol/schema
      if (!url.protocol || url.protocol === ':') {
        return false;
      }

      // For URLs with standard protocols, validate the domain structure
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        const [domain, ext] = url.hostname.split('.');
        if (!domain || !ext || domain.length < 2 || ext.length < 2) {
          return false;
        }
      }

      return true;
    } catch (error) {
      return false; // If URL constructor fails, the URL is not valid
    }
  });

export function validateOnrampDomain(url: string) {
  try {
    onrampDomainSchema.validateSync(url);
    return true;
  } catch (error) {
    return false;
  }
}
